import Image from 'next/image'
import {FC, useState} from 'react'
import {Settings} from 'react-slick'

import {CAROUSEL_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {
  MOBILE_MAX_WIDTH,
  MOBILE_SCREENSIZE,
  TABLET_MAX_WIDTH,
  TABLET_SCREENSIZE,
  USER_TABLET_MAX_WIDTH,
} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'

import Carousel from './Carousel'
import {Logo} from './types'

type LogoCarouselProps = {
  logos: Logo[]
  slidesToShow?: number
}

export const LogoCarousel: FC<React.PropsWithChildren<LogoCarouselProps>> = ({
  logos,
  slidesToShow = 4,
}) => {
  const settings: Settings = {
    infinite: true,
    speed: CAROUSEL_TRANSITION,
    slidesToShow: slidesToShow,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: false,
    responsive: [
      {
        breakpoint: TABLET_MAX_WIDTH,
        settings: {
          dots: true,
        },
      },
      {
        breakpoint: USER_TABLET_MAX_WIDTH,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
          dots: true,
        },
      },
      {
        breakpoint: MOBILE_MAX_WIDTH,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
          dots: true,
        },
      },
    ],
  }

  const [clientXonMouseDown, setClientXonMouseDown] = useState(null)
  const [clientYonMouseDown, setClientYonMouseDown] = useState(null)

  const handleOnMouseDown = (e: any) => {
    setClientXonMouseDown(e.clientX)
    setClientYonMouseDown(e.clientY)
    e.preventDefault()
  }

  const handleOnClick = (e: any, imageAlt: string) => {
    e.stopPropagation()
    if (clientXonMouseDown !== e.clientX || clientYonMouseDown !== e.clientY) {
      e.preventDefault()
    } else {
      analytics.track('Carousel logo clicked', {logo: imageAlt})
    }
  }

  return (
    <>
      <div className="logos">
        <Carousel settings={settings} onSwipe={() => analytics.track('Carousel logo swiped')}>
          {/* Eager so autoplay never shows an empty slide, but low priority: React preloads every
              other eager image, and 17 logo preloads competed with the hero image. */}
          {logos.map(({imageSrc, imageAlt, linkHref}) =>
            linkHref ? (
              <a
                key={imageAlt}
                href={linkHref}
                rel="noopener noreferrer"
                target="_blank"
                className="logo-wrapper"
                onMouseDown={(e) => handleOnMouseDown(e)}
                onClick={(e) => handleOnClick(e, imageAlt)}
              >
                <Image
                  src={imageSrc}
                  alt={`Logo of ${imageAlt}`}
                  className="client-logo"
                  width={175}
                  height={64}
                  loading="eager"
                  fetchPriority="low"
                />
              </a>
            ) : (
              <div key={imageAlt} className="logo-wrapper">
                <Image
                  src={imageSrc}
                  alt={`Logo of ${imageAlt}`}
                  className="client-logo"
                  width={175}
                  height={64}
                  loading="eager"
                  fetchPriority="low"
                />
              </div>
            ),
          )}
        </Carousel>
      </div>
      <style jsx>{`
        .logos {
          width: 100%;
          max-width: 100%;
        }
        .logo-wrapper {
          height: 64px;
        }
        @media ${TABLET_SCREENSIZE} {
          .logos {
            text-align: center;
            margin-left: auto;
            margin-right: auto;
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .logo-wrapper {
            height: auto;
          }
        }
      `}</style>
      <style jsx global>{`
        .logos .carousel .slick-slide {
          display: flex;
          justify-content: center;
        }
        .logos .carousel .slick-slide > div {
          display: inline-flex;
          align-items: center;
        }
        .logos .slick-slider {
          max-width: 1000px;
        }
        .logos .slick-dots {
          margin-top: ${theme.spacing[3]};
        }
        @media ${TABLET_SCREENSIZE} {
          .logos .slick-dots {
            margin-top: 0;
          }
        }
      `}</style>
    </>
  )
}
